import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import {
  requestCameraPermission,
  queryCameraPermission,
} from "../../services/camera-permission";
import { YouTubePlayer } from "./youtube-player";

// Shown before the scanner when the order carries a tutorial video.
export const TutorialVideoView = ({
  url,
  title,
  onPermissionGranted,
  onContinue,
}) => {
  const [hide, setHide] = useState(false);

  // Request for camera access on load.
  const requestingRef = useRef(false);
  useEffect(() => {
    let cancelled = false;

    // Inline permission function
    const triggerCameraPermission = async () => {
      // Don't make double requests.
      if (requestingRef.current) {
        return;
      }

      // Request for camera permission.
      requestingRef.current = true;
      const state = await requestCameraPermission();
      requestingRef.current = false;
      const granted = state === "granted";

      // Handle permission granted.
      if (!cancelled && granted && onPermissionGranted) {
        onPermissionGranted();
      }
    };

    // Query camera permission.
    queryCameraPermission().then((state) => {
      if (cancelled) {
        return;
      }
      const alreadyGranted = state === "granted";
      const canPrompt = state === "prompt";
      if (alreadyGranted && onPermissionGranted) {
        onPermissionGranted();
      } else if (canPrompt) {
        triggerCameraPermission();
      }
      // "denied": Nothing needs to be done.
      // -- The scanner will show its own error once activated.
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The component
  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        backgroundColor: "#000",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          pt: "max(24px, env(safe-area-inset-top))",
          px: 2,
          textAlign: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{ color: "#fff", textTransform: "capitalize" }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5 }}
        >
          Watch this short tutorial before scanning
        </Typography>
      </Box>

      {/* video, centered, 16:9 */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 900,
            aspectRatio: "16 / 9",
            backgroundColor: "#000",
          }}
        >
          <YouTubePlayer url={url} />
        </Box>
      </Box>

      <Box
        sx={{
          px: 3,
          pt: 2,
          pb: "max(24px, env(safe-area-inset-bottom))",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={hide}
              onChange={(e) => setHide(e.target.checked)}
              sx={{ color: "rgba(255,255,255,0.7)" }}
            />
          }
          label={`Hide Tutorial for ${title}`}
          sx={{ color: "#fff", m: 0 }}
        />
        <Button
          variant="contained"
          size="large"
          onClick={() => onContinue(hide)}
          sx={{ borderRadius: 8, px: 5, py: 1.5 }}
        >
          Start scanning
        </Button>
      </Box>
    </Box>
  );
};

TutorialVideoView.propTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string,
  onPermissionGranted: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};
